// src/utils/passports/validate.ts
import { poseidon2 as poseidon24, poseidon5 as poseidon55 } from "poseidon-lite";

// src/constants/constants.ts
var API_URL = "https://api.self.xyz";
var API_URL_STAGING = "https://api.staging.self.xyz";
var ID_CARD_ATTESTATION_ID = "2";
var MAX_BYTES_IN_FIELD = 31;
var PASSPORT_ATTESTATION_ID = "1";
var max_csca_bytes = 1792;
var max_dsc_bytes = 1792;

// src/utils/certificate_parsing/parseCertificateSimple.ts
import * as asn1js2 from "asn1js";
import { Certificate, RSAPublicKey, RSASSAPSSParams } from "pkijs";

// src/utils/certificate_parsing/curves.ts
function getCurveForElliptic(curveName) {
  const curves = {
    secp224r1: "p224",
    secp256r1: "p256",
    secp384r1: "p384",
    secp521r1: "p521",
    brainpoolP224r1: "brainpoolP224r1",
    brainpoolP256r1: "brainpoolP256r1",
    brainpoolP384r1: "brainpoolP384r1",
    brainpoolP512r1: "brainpoolP512r1"
  };
  if (!curves[curveName]) {
    throw new Error("Invalid curve: " + curveName);
  }
  return curves[curveName];
}
function getECDSACurveBits(curveName) {
  const curveBits = {
    secp224r1: 224,
    secp256r1: 256,
    secp384r1: 384,
    secp521r1: 521,
    brainpoolP224r1: 224,
    brainpoolP256r1: 256,
    brainpoolP384r1: 384,
    brainpoolP512r1: 512
  };
  if (curveName in curveBits) {
    return curveBits[curveName].toString();
  }
  console.log("\x1B[31m%s\x1B[0m", `curve name ${curveName} not found in curveBits`);
  return "unknown";
}
function identifyCurve(params) {
  const normalizedParams = {
    p: normalizeHex(params.p),
    a: normalizeHex(params.a),
    b: normalizeHex(params.b),
    G: normalizeHex(params.G),
    n: normalizeHex(params.n),
    h: normalizeHex(params.h)
  };
  for (const curve of standardCurves) {
    if (normalizedParams.p === normalizeHex(curve.p) && normalizedParams.a === normalizeHex(curve.a) && normalizedParams.b === normalizeHex(curve.b) && normalizedParams.G === normalizeHex(curve.G) && normalizedParams.n === normalizeHex(curve.n) && normalizedParams.h === normalizeHex(curve.h)) {
      return curve.name;
    }
  }
  console.log("Unknown curve:", normalizedParams);
  return "Unknown curve";
}
function normalizeHex(hex) {
  return hex.toLowerCase().replace(/^0x/, "").replace(/^00/, "");
}
var standardCurves = [
  {
    name: "secp192r1",
    p: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFFFFFFFFFFFF",
    a: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFFFFFFFFFFFC",
    b: "64210519E59C80E70FA7E9AB72243049FEB8DEECC146B9B1",
    G: "04188DA80EB03090F67CBF20EB43A18800F4FF0AFD82FF101207192B95FFC8DA78631011ED6B24CDD573F977A11E794811",
    n: "FFFFFFFFFFFFFFFFFFFFFFFF99DEF836146BC9B1B4D22831",
    h: "01"
  },
  {
    name: "secp224r1",
    p: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF000000000000000000000001",
    a: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFFFFFFFFFFFFFFFFFFFE",
    b: "B4050A850C04B3ABF54132565044B0B7D7BFD8BA270B39432355FFB4",
    G: "04B70E0CBD6BB4BF7F321390B94A03C1D356C21122343280D6115C1D21BD376388B5F723FB4C22DFE6CD4375A05A07476444D5819985007E34",
    n: "FFFFFFFFFFFFFFFFFFFFFFFFFFFF16A2E0B8F03E13DD29455C5C2A3D",
    h: "01"
  },
  {
    name: "secp256r1",
    p: "FFFFFFFF00000001000000000000000000000000FFFFFFFFFFFFFFFFFFFFFFFF",
    a: "FFFFFFFF00000001000000000000000000000000FFFFFFFFFFFFFFFFFFFFFFFC",
    b: "5AC635D8AA3A93E7B3EBBD55769886BC651D06B0CC53B0F63BCE3C3E27D2604B",
    G: "046B17D1F2E12C4247F8BCE6E563A440F277037D812DEB33A0F4A13945D898C2964FE342E2FE1A7F9B8EE7EB4A7C0F9E162BCE33576B315ECECBB6406837BF51F5",
    n: "FFFFFFFF00000000FFFFFFFFFFFFFFFFBCE6FAADA7179E84F3B9CAC2FC632551",
    h: "01"
  },
  {
    name: "secp384r1",
    p: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFFFF0000000000000000FFFFFFFF",
    a: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFFFF0000000000000000FFFFFFFC",
    b: "B3312FA7E23EE7E4988E056BE3F82D19181D9C6EFE8141120314088F5013875AC656398D8A2ED19D2A85C8EDD3EC2AEF",
    G: "04AA87CA22BE8B05378EB1C71EF320AD746E1D3B628BA79B9859F741E082542A385502F25DBF55296C3A545E3872760AB73617DE4A96262C6F5D9E98BF9292DC29F8F41DBD289A147CE9DA3113B5F0B8C00A60B1CE1D7E819D7A431D7C90EA0E5F",
    n: "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFC7634D81F4372DDF581A0DB248B0A77AECEC196ACCC52973",
    h: "01"
  },
  {
    name: "secp521r1",
    p: "01FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF",
    a: "01FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFC",
    b: "0051953EB9618E1C9A1F929A21A0B68540EEA2DA725B99B315F3B8B489918EF109E156193951EC7E937B1652C0BD3BB1BF073573DF883D2C34F1EF451FD46B503F00",
    G: "0400C6858E06B70404E9CD9E3ECB662395B4429C648139053FB521F828AF606B4D3DBAA14B5E77EFE75928FE1DC127A2FFA8DE3348B3C1856A429BF97E7E31C2E5BD66011839296A789A3BC0045C8A5FB42C7D1BD998F54449579B446817AFBD17273E662C97EE72995EF42640C550B9013FAD0761353C7086A272C24088BE94769FD16650",
    n: "01FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFA51868783BF2F966B7FCC0148F709A5D03BB5C9B8899C47AEBB6FB71E91386409",
    h: "01"
  },
  {
    name: "brainpoolP192r1",
    p: "C302F41D932A36CDA7A3463093D18DB78FCE476DE1A86297",
    a: "6A91174076B1E0E19C39C031FE8685C1CAE040E5C69A28EF",
    b: "469A28EF7C28CCA3DC721D044F4496BCCA7EF4146FBF25C9",
    G: "04C0A0647EAA7F9B8EE7C1AC4D77FC94CA14B690866ABD5BB88B5F4828C1490002E6773FA2FA299B8F",
    n: "C302F41D932A36CDA7A3463093D18DB78FCE476DE1A86294",
    h: "01"
  },
  {
    name: "brainpoolP224r1",
    p: "d7c134aa264366862a18302575d1d787b09f075797da89f57ec8c0ff",
    a: "68a5e62ca9ce6c1c299803a6c1530b514e182ad8b0042a59cad29f43",
    b: "2580f63ccfe44138870713b1a92369e33e2135d266dbb372386c400b",
    G: "040d9029ad2c7e5cf4340823b2a87dc68c9e4ce3174c1e6efdee12c07d58aa56f772c0726f24c6b89e4ecdac24354b9e99caa3f6d3761402cd",
    n: "d7c134aa264366862a18302575d0fb98d116bc4b6ddebca3a5a7939f",
    h: "01"
  },
  {
    name: "brainpoolP256r1",
    p: "A9FB57DBA1EEA9BC3E660A909D838D726E3BF623D52620282013481D1F6E5377",
    a: "7D5A0975FC2C3057EEF67530417AFFE7FB8055C126DC5C6CE94A4B44F330B5D9",
    b: "26DC5C6CE94A4B44F330B5D9BBD77CBF958416295CF7E1CE6BCCDC18FF8C07B6",
    G: "048BD2AEB9CB7E57CB2C4B482FFC81B7AFB9DE27E1E3BD23C23A4453BD9ACE3262547EF835C3DAC4FD97F8461A14611DC9C27745132DED8E545C1D54C72F046997",
    n: "A9FB57DBA1EEA9BC3E660A909D838D718C397AA3B561A6F7901E0E82974856A7",
    h: "01"
  },
  {
    name: "brainpoolP384r1",
    p: "8CB91E82A3386D280F5D6F7E50E641DF152F7109ED5456B412B1DA197FB71123ACD3A729901D1A71874700133107EC53",
    a: "7BC382C63D8C150C3C72080ACE05AFA0C2BEA28E4FB22787139165EFBA91F90F8AA5814A503AD4EB04A8C7DD22CE2826",
    b: "04A8C7DD22CE28268B39B55416F0447C2FB77DE107DCD2A62E880EA53EEB62D57CB4390295DBC9943AB78696FA504C11",
    G: "041D1C64F068CF45FFA2A63A81B7C13F6B8847A3E77EF14FE3DB7FCAFE0CBD10E8E826E03436D646AAEF87B2E247D4AF1E8ABE1D7520F9C2A45CB1EB8E95CFD55262B70B29FEEC5864E19C054FF99129280E4646217791811142820341263C5315",
    n: "8CB91E82A3386D280F5D6F7E50E641DF152F7109ED5456B31F166E6CAC0425A7CF3AB6AF6B7FC3103B883202E9046565",
    h: "01"
  },
  {
    name: "brainpoolP512r1",
    p: "AADD9DB8DBE9C48B3FD4E6AE33C9FC07CB308DB3B3C9D20ED6639CCA703308717D4D9B009BC66842AECDA12AE6A380E62881FF2F2D82C68528AA6056583A48F3",
    a: "7830A3318B603B89E2327145AC234CC594CBDD8D3DF91610A83441CAEA9863BC2DED5D5AA8253AA10A2EF1C98B9AC8B57F1117A72BF2C7B9E7C1AC4D77FC94CA",
    b: "3DF91610A83441CAEA9863BC2DED5D5AA8253AA10A2EF1C98B9AC8B57F1117A72BF2C7B9E7C1AC4D77FC94CADC083E67984050B75EBAE5DD2809BD638016F723",
    G: "0481AEE4BDD82ED9645A21322E9C4C6A9385ED9F70B5D916C1B43B62EEF4D0098EFF3B1F78E2D0D48D50D1687B93B97D5F7C6D5047406A5E688B352209BCB9F8227DDE385D566332ECC0EABFA9CF7822FDF209F70024A57B1AA000C55B881F8111B2DCDE494A5F485E5BCA4BD88A2763AED1CA2B2FA8F0540678CD1E0F3AD80892",
    n: "AADD9DB8DBE9C48B3FD4E6AE33C9FC07CB308DB3B3C9D20ED6639CCA70330870553E5C414CA92619418661197FAC10471DB1D381085DDADDB58796829CA90069",
    h: "01"
  }
];

// src/utils/certificate_parsing/elliptic.ts
import elliptic from "elliptic";
import hash from "hash.js";
function initElliptic() {
  const curves = elliptic.curves;
  const PresetCurve = elliptic.curves.PresetCurve;
  function defineCurve(name, options) {
    Object.defineProperty(curves, name, {
      configurable: true,
      enumerable: true,
      get: function() {
        const curve = new PresetCurve(options);
        Object.defineProperty(curves, name, {
          configurable: true,
          enumerable: true,
          value: curve
        });
        return curve;
      }
    });
  }
  defineCurve("brainpoolP224r1", {
    type: "short",
    prime: null,
    p: "d7c134aa 26436686 2a183025 75d1d787 b09f0757 97da89f5 7ec8c0ff",
    a: "68a5e62c a9ce6c1c 299803a6 c1530b51 4e182ad8 b0042a59 cad29f43",
    b: "2580f63c cfe44138 870713b1 a92369e3 3e2135d2 66dbb372 386c400b",
    n: "d7c134aa 26436686 2a183025 75d0fb98 d116bc4b 6ddebca3 a5a7939f",
    hash: hash.sha1,
    gRed: false,
    g: [
      "0d9029ad 2c7e5cf4 340823b2 a87dc68c 9e4ce317 4c1e6efd ee12c07d",
      "58aa56f7 72c0726f 24c6b89e 4ecdac24 354b9e99 caa3f6d3 761402cd"
    ]
  });
  defineCurve("brainpoolP256r1", {
    type: "short",
    prime: null,
    p: "a9fb57db a1eea9bc 3e660a90 9d838d72 6e3bf623 d5262028 2013481d 1f6e5377",
    a: "7d5a0975 fc2c3057 eef67530 417affe7 fb8055c1 26dc5c6c e94a4b44 f330b5d9",
    b: "26dc5c6c e94a4b44 f330b5d9 bbd77cbf 95841629 5cf7e1ce 6bccdc18 ff8c07b6",
    n: "a9fb57db a1eea9bc 3e660a90 9d838d71 8c397aa3 b561a6f7 901e0e82 974856a7",
    hash: hash.sha256,
    gRed: false,
    g: [
      "8bd2aeb9 cb7e57cb 2c4b482f fc81b7af b9de27e1 e3bd23c2 3a4453bd 9ace3262",
      "547ef835 c3dac4fd 97f8461a 14611dc9 c2774513 2ded8e54 5c1d54c7 2f046997"
    ]
  });
  defineCurve("brainpoolP384r1", {
    type: "short",
    prime: null,
    p: "8cb91e82 a3386d28 0f5d6f7e 50e641df 152f7109 ed5456b4 12b1da19 7fb71123 acd3a729 901d1a71 87470013 3107ec53",
    a: "7bc382c6 3d8c150c 3c72080a ce05afa0 c2bea28e 4fb22787 139165ef ba91f90f 8aa5814a 503ad4eb 04a8c7dd 22ce2826",
    b: "04a8c7dd 22ce2826 8b39b554 16f0447c 2fb77de1 07dcd2a6 2e880ea5 3eeb62d5 7cb43902 95dbc994 3ab78696 fa504c11",
    n: "8cb91e82 a3386d28 0f5d6f7e 50e641df 152f7109 ed5456b3 1f166e6c ac0425a7 cf3ab6af 6b7fc310 3b883202 e9046565",
    hash: hash.sha384,
    gRed: false,
    g: [
      "1d1c64f0 68cf45ff a2a63a81 b7c13f6b 8847a3e7 7ef14fe3 db7fcafe 0cbd10e8 e826e034 36d646aa ef87b2e2 47d4af1e",
      "8abe1d75 20f9c2a4 5cb1eb8e 95cfd552 62b70b29 feec5864 e19c054f f9912928 0e464621 77918111 42820341 263c5315"
    ]
  });
  defineCurve("brainpoolP512r1", {
    type: "short",
    prime: null,
    p: "aadd9db8 dbe9c48b 3fd4e6ae 33c9fc07 cb308db3 b3c9d20e d6639cca 70330871 7d4d9b00 9bc66842 aecda12a e6a380e6 2881ff2f 2d82c685 28aa6056 583a48f3",
    a: "7830a331 8b603b89 e2327145 ac234cc5 94cbdd8d 3df91610 a83441ca ea9863bc 2ded5d5a a8253aa1 0a2ef1c9 8b9ac8b5 7f1117a7 2bf2c7b9 e7c1ac4d 77fc94ca",
    b: "3df91610 a83441ca ea9863bc 2ded5d5a a8253aa1 0a2ef1c9 8b9ac8b5 7f1117a7 2bf2c7b9 e7c1ac4d 77fc94ca dc083e67 984050b7 5ebae5dd 2809bd63 8016f723",
    n: "aadd9db8 dbe9c48b 3fd4e6ae 33c9fc07 cb308db3 b3c9d20e d6639cca 70330870 553e5c41 4ca92619 41866119 7fac1047 1db1d381 085ddadd b5879682 9ca90069",
    hash: hash.sha512,
    gRed: false,
    g: [
      "81aee4bd d82ed964 5a21322e 9c4c6a93 85ed9f70 b5d916c1 b43b62ee f4d0098e ff3b1f78 e2d0d48d 50d1687b 93b97d5f 7c6d5047 406a5e68 8b352209 bcb9f822",
      "7dde385d 566332ec c0eabfa9 cf7822fd f209f700 24a57b1a a000c55b 881f8111 b2dcde49 4a5f485e 5bca4bd8 8a2763ae d1ca2b2f a8f05406 78cd1e0f 3ad80892"
    ]
  });
  return elliptic;
}

// src/utils/certificate_parsing/oids.ts
function getFriendlyName(oid) {
  return getFriendlyNameSecpCurves(oidMap[oid]) || "Unknown Algorithm";
}
function getSecpFromNist(nist) {
  switch (nist) {
    case "nistP224":
      return "secp224r1";
    case "nistP256":
      return "secp256r1";
    case "nistP384":
      return "secp384r1";
    case "nistP521":
      return "secp521r1";
  }
  return nist;
}
function getFriendlyNameSecpCurves(friendlyName) {
  return mapSecpCurves[friendlyName] || friendlyName;
}
var mapSecpCurves = {
  ECDSA_224: "secp224r1",
  ECDSA_P256: "secp256r1",
  ECDSA_P384: "secp384r1",
  ECDSA_P521: "secp521r1"
};
var oidMap = {
  "1.2.840.113549.3.7": "3des",
  "2.16.840.1.101.3.4.1.2": "aes128",
  "2.16.840.1.101.3.4.1.5": "aes128wrap",
  "2.16.840.1.101.3.4.1.22": "aes192",
  "2.16.840.1.101.3.4.1.25": "aes192wrap",
  "2.16.840.1.101.3.4.1.42": "aes256",
  "2.16.840.1.101.3.4.1.45": "aes256wrap",
  "1.3.36.3.3.2.8.1.1.1": "brainpoolP160r1",
  "1.3.36.3.3.2.8.1.1.2": "brainpoolP160t1",
  "1.3.36.3.3.2.8.1.1.3": "brainpoolP192r1",
  "1.3.36.3.3.2.8.1.1.4": "brainpoolP192t1",
  "1.3.36.3.3.2.8.1.1.5": "brainpoolP224r1",
  "1.3.36.3.3.2.8.1.1.6": "brainpoolP224t1",
  "1.3.36.3.3.2.8.1.1.7": "brainpoolP256r1",
  "1.3.36.3.3.2.8.1.1.8": "brainpoolP256t1",
  "1.3.36.3.3.2.8.1.1.9": "brainpoolP320r1",
  "1.3.36.3.3.2.8.1.1.10": "brainpoolP320t1",
  "1.3.36.3.3.2.8.1.1.11": "brainpoolP384r1",
  "1.3.36.3.3.2.8.1.1.12": "brainpoolP384t1",
  "1.3.36.3.3.2.8.1.1.13": "brainpoolP512r1",
  "1.3.36.3.3.2.8.1.1.14": "brainpoolP512t1",
  "2.5.4.6": "C",
  "1.2.840.113549.1.9.16.3.6": "CMS3DESwrap",
  "1.2.840.113549.1.9.16.3.7": "CMSRC2wrap",
  "2.5.4.3": "CN",
  "1.3.6.1.5.5.7.2.1": "CPS",
  "0.9.2342.19200300.100.1.25": "DC",
  "1.3.14.3.2.7": "des",
  "2.5.4.13": "Description",
  "1.2.840.10046.2.1": "DH",
  "2.5.4.46": "dnQualifier",
  "1.2.840.10040.4.1": "DSA",
  "1.3.14.3.2.27": "dsaSHA1",
  "1.2.840.113549.1.9.1": "E",
  "1.2.156.11235.1.1.2.1": "ec192wapi",
  "1.2.840.10045.2.1": "ECC",
  "1.3.133.16.840.63.0.2": "ECDH_STD_SHA1_KDF",
  "1.3.132.1.11.1": "ECDH_STD_SHA256_KDF",
  "1.3.132.1.11.2": "ECDH_STD_SHA384_KDF",
  "1.2.840.10045.3.1.7": "ECDSA_P256",
  "1.3.132.0.34": "ECDSA_P384",
  "1.3.132.0.35": "ECDSA_P521",
  "1.2.840.113549.1.9.16.3.5": "ESDH",
  "2.5.4.42": "G",
  "2.5.4.43": "I",
  "2.5.4.7": "L",
  "1.2.840.113549.2.2": "md2",
  "1.2.840.113549.1.1.2": "md2RSA",
  "1.2.840.113549.2.4": "md4",
  "1.2.840.113549.1.1.3": "md4RSA",
  "1.2.840.113549.2.5": "md5",
  "1.2.840.113549.1.1.4": "md5RSA",
  "1.2.840.113549.1.1.8": "mgf1",
  "2.16.840.1.101.2.1.1.20": "mosaicKMandUpdSig",
  "2.16.840.1.101.2.1.1.19": "mosaicUpdatedSig",
  "1.2.840.10045.3.1.1": "nistP192",
  "1.3.132.0.33": "nistP224",
  "1.3.6.1.5.5.7.6.2": "NO_SIGN",
  "2.5.4.10": "O",
  "2.5.4.11": "OU",
  "2.5.4.20": "Phone",
  "2.5.4.18": "POBox",
  "2.5.4.17": "PostalCode",
  "1.2.840.113549.3.2": "rc2",
  "1.2.840.113549.3.4": "rc4",
  "1.2.840.113549.1.1.1": "RSA",
  "1.2.840.113549.1.1.7": "RSAES_OAEP",
  "1.2.840.113549.1.1.10": "RSASSA_PSS",
  "2.5.4.8": "S",
  "1.3.132.0.9": "secP160k1",
  "1.3.132.0.8": "secP160r1",
  "1.3.132.0.30": "secP160r2",
  "1.3.132.0.31": "secP192k1",
  "1.3.132.0.32": "secP224k1",
  "1.3.132.0.10": "secP256k1",
  "2.5.4.5": "SERIALNUMBER",
  "1.3.14.3.2.26": "sha1",
  "1.2.840.10040.4.3": "sha1DSA",
  "1.2.840.10045.4.1": "sha1ECDSA",
  "1.2.840.113549.1.1.5": "sha1RSA",
  "1.2.840.10045.4.3.1": "sha224ECDSA",
  "1.2.840.113549.1.1.14": "sha224RSA",
  "2.16.840.1.101.3.4.2.1": "sha256",
  "1.2.840.10045.4.3.2": "sha256ECDSA",
  "1.2.840.113549.1.1.11": "sha256RSA",
  "2.16.840.1.101.3.4.2.2": "sha384",
  "1.2.840.10045.4.3.3": "sha384ECDSA",
  "1.2.840.113549.1.1.12": "sha384RSA",
  "2.16.840.1.101.3.4.2.3": "sha512",
  "1.2.840.10045.4.3.4": "sha512ECDSA",
  "1.2.840.113549.1.1.13": "sha512RSA",
  "2.5.4.4": "SN",
  "1.2.840.10045.4.3": "specifiedECDSA",
  "2.5.4.9": "STREET",
  "2.5.4.12": "T",
  "2.23.133.2.1": "TPMManufacturer",
  "2.23.133.2.2": "TPMModel",
  "2.23.133.2.3": "TPMVersion",
  "2.23.43.1.4.9": "wtls9",
  "2.5.4.24": "X21Address",
  "1.2.840.10045.3.1.2": "x962P192v2",
  "1.2.840.10045.3.1.3": "x962P192v3",
  "1.2.840.10045.3.1.4": "x962P239v1",
  "1.2.840.10045.3.1.5": "x962P239v2",
  "1.2.840.10045.3.1.6": "x962P239v3"
};

// src/utils/certificate_parsing/utils.ts
import * as asn1js from "asn1js";
import { sha256 } from "js-sha256";
function getIssuerCountryCode(cert) {
  const issuerRDN = cert.issuer.typesAndValues;
  let issuerCountryCode = "";
  for (const rdn of issuerRDN) {
    if (rdn.type === "2.5.4.6") {
      issuerCountryCode = rdn.value.valueBlock.value;
      break;
    }
  }
  return issuerCountryCode.toUpperCase();
}
var getSubjectKeyIdentifier = (cert) => {
  const subjectKeyIdentifier = cert.extensions.find((ext) => ext.extnID === "2.5.29.14");
  if (subjectKeyIdentifier) {
    let skiValue = Buffer.from(subjectKeyIdentifier.extnValue.valueBlock.valueHexView).toString(
      "hex"
    );
    skiValue = skiValue.replace(/^(?:30(?:16|1E|22|32|42))?(?:04(?:08|14|1C|20|30|40))?/, "");
    return skiValue;
  } else {
    const hash3 = sha256.create();
    hash3.update(cert.tbsView);
    return hash3.hex();
  }
};

// src/utils/certificate_parsing/parseCertificateSimple.ts
var getAuthorityKeyIdentifier = (cert) => {
  const authorityKeyIdentifier = cert.extensions.find((ext) => ext.extnID === "2.5.29.35");
  if (authorityKeyIdentifier) {
    let akiValue = Buffer.from(authorityKeyIdentifier.extnValue.valueBlock.valueHexView).toString(
      "hex"
    );
    const sequenceMatch = akiValue.match(/^30([0-9a-f]{2}|8[0-9a-f][0-9a-f])/i);
    if (sequenceMatch) {
    }
    const keyIdMatch = akiValue.match(/80([0-9a-f]{2})/i);
    if (keyIdMatch) {
      const keyIdLength = parseInt(keyIdMatch[1], 16);
      const startIndex = akiValue.indexOf(keyIdMatch[0]) + 4;
      akiValue = akiValue.slice(startIndex, startIndex + keyIdLength * 2);
      return akiValue.toUpperCase();
    }
  }
  return null;
};
function getParamsRSA(cert) {
  const publicKeyValue = cert.subjectPublicKeyInfo.parsedKey;
  const modulusBytes = publicKeyValue.modulus.valueBlock.valueHexView;
  const modulusHex = Buffer.from(modulusBytes).toString("hex");
  const exponentBigInt = publicKeyValue.publicExponent.toBigInt();
  const exponentDecimal = exponentBigInt.toString();
  const actualBits = modulusBytes.length * 8;
  return {
    modulus: modulusHex,
    exponent: exponentDecimal,
    bits: actualBits.toString()
  };
}
function getParamsRSAPSS(cert) {
  const spki = cert.subjectPublicKeyInfo;
  const spkiValueHex = spki.subjectPublicKey.valueBlock.valueHexView;
  const asn1PublicKey = asn1js2.fromBER(spkiValueHex);
  if (asn1PublicKey.offset === -1) {
    throw new Error("Error parsing public key ASN.1 structure");
  }
  const rsaPublicKey = new RSAPublicKey({ schema: asn1PublicKey.result });
  const modulusBytes = rsaPublicKey.modulus.valueBlock.valueHexView;
  const modulusHex = Buffer.from(modulusBytes).toString("hex");
  const exponentBigInt = rsaPublicKey.publicExponent.toBigInt();
  const exponentDecimal = exponentBigInt.toString();
  const actualBits = modulusBytes.length * 8;
  const sigAlgParams = cert.signatureAlgorithm.algorithmParams;
  const pssParams = new RSASSAPSSParams({ schema: sigAlgParams });
  const hashAlgorithm = getFriendlyName(pssParams.hashAlgorithm.algorithmId);
  const mgf = getFriendlyName(pssParams.maskGenAlgorithm.algorithmId);
  return {
    modulus: modulusHex,
    exponent: exponentDecimal,
    bits: actualBits.toString(),
    hashAlgorithm,
    mgf,
    saltLength: pssParams.saltLength.toString()
  };
}
function getCertificateFromPem(pemContent) {
  const pemFormatted = pemContent.replace(/(-----(BEGIN|END) CERTIFICATE-----|\n|\r)/g, "");
  const binary = Buffer.from(pemFormatted, "base64");
  const arrayBuffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(arrayBuffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary[i];
  }
  const asn1 = asn1js2.fromBER(arrayBuffer);
  if (asn1.offset === -1) {
    throw new Error(`ASN.1 parsing error: ${asn1.result.error}`);
  }
  return new Certificate({ schema: asn1.result });
}
function getHashAlgorithm(rawSignatureAlgorithm) {
  const input = rawSignatureAlgorithm.toLowerCase();
  const patterns = [/sha-?1/i, /sha-?224/i, /sha-?256/i, /sha-?384/i, /sha-?512/i];
  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) {
      return match[0].replace("-", "");
    }
  }
  return "unknown";
}
function getParamsECDSA(cert) {
  try {
    const algorithmParams = cert.subjectPublicKeyInfo.algorithm.algorithmParams;
    if (!algorithmParams) {
      console.error("No algorithm params found");
      return {
        curve: "Unknown",
        params: {},
        bits: "Unknown",
        x: "Unknown",
        y: "Unknown"
      };
    }
    let curveName, bits, x, y = "Unknown";
    const curveParams = {};
    if (algorithmParams instanceof asn1js2.ObjectIdentifier) {
      const curveOid = algorithmParams.valueBlock.toString();
      curveName = getSecpFromNist(getFriendlyName(curveOid)) || "Unknown";
      bits = getECDSACurveBits(curveName);
    } else {
      const params = asn1js2.fromBER(algorithmParams.valueBeforeDecodeView).result;
      const valueBlock = params.valueBlock;
      if (valueBlock.value && valueBlock.value.length >= 5) {
        const curveParams2 = {};
        const fieldId = valueBlock.value[1];
        if (fieldId && fieldId.valueBlock && fieldId.valueBlock.value) {
          const fieldType = fieldId.valueBlock.value[0];
          const prime = fieldId.valueBlock.value[1];
          curveParams2.p = Buffer.from(prime.valueBlock.valueHexView).toString("hex");
        }
        const curveCoefficients = valueBlock.value[2];
        if (curveCoefficients && curveCoefficients.valueBlock && curveCoefficients.valueBlock.value) {
          const a = curveCoefficients.valueBlock.value[0];
          const b = curveCoefficients.valueBlock.value[1];
          curveParams2.a = Buffer.from(a.valueBlock.valueHexView).toString("hex");
          curveParams2.b = Buffer.from(b.valueBlock.valueHexView).toString("hex");
        }
        const basePoint = valueBlock.value[3];
        if (basePoint && basePoint.valueBlock) {
          curveParams2.G = Buffer.from(basePoint.valueBlock.valueHexView).toString("hex");
        }
        const order = valueBlock.value[4];
        if (order && order.valueBlock) {
          curveParams2.n = Buffer.from(order.valueBlock.valueHexView).toString("hex");
        }
        if (valueBlock.value.length >= 6) {
          const cofactor = valueBlock.value[5];
          if (cofactor && cofactor.valueBlock) {
            curveParams2.h = Buffer.from(cofactor.valueBlock.valueHexView).toString("hex");
          }
        } else {
          curveParams2.h = "01";
        }
        const identifiedCurve = identifyCurve(curveParams2);
        curveName = identifiedCurve;
        bits = getECDSACurveBits(curveName);
      } else {
        if (valueBlock.value) {
          console.log(valueBlock.value);
        } else {
          console.log("No value block found");
        }
      }
    }
    const publicKeyBuffer = cert.subjectPublicKeyInfo.subjectPublicKey.valueBlock.valueHexView;
    if (publicKeyBuffer && curveName !== "Unknown") {
      const elliptic2 = initElliptic();
      const ec = new elliptic2.ec(getCurveForElliptic(curveName));
      const key = ec.keyFromPublic(publicKeyBuffer);
      const x_point = key.getPublic().getX().toString("hex");
      const y_point = key.getPublic().getY().toString("hex");
      if (curveName === "secp521r1" || curveName === "brainpoolP521r1") {
        x = x_point.padStart(132, "0");
        y = y_point.padStart(132, "0");
      } else {
        x = x_point.length % 2 === 0 ? x_point : "0" + x_point;
        y = y_point.length % 2 === 0 ? y_point : "0" + y_point;
      }
    }
    return { curve: curveName, params: curveParams, bits, x, y };
  } catch (error) {
    console.error("Error parsing EC parameters:", error);
    return {
      curve: "Error",
      params: {},
      bits: "Unknown",
      x: "Unknown",
      y: "Unknown"
    };
  }
}
function getTBSBytesForge(certificate) {
  return Array.from(certificate.tbsView.map((byte) => parseInt(byte.toString(16), 16)));
}
function parseCertificateSimple(pem) {
  const certificateData = {
    id: "",
    issuer: "",
    validity: {
      notBefore: "",
      notAfter: ""
    },
    subjectKeyIdentifier: "",
    authorityKeyIdentifier: "",
    signatureAlgorithm: "",
    hashAlgorithm: "",
    publicKeyDetails: void 0,
    tbsBytes: void 0,
    tbsBytesLength: "",
    rawPem: "",
    rawTxt: "",
    publicKeyAlgoOID: ""
  };
  try {
    const cert = getCertificateFromPem(pem);
    certificateData.tbsBytes = getTBSBytesForge(cert);
    certificateData.tbsBytesLength = certificateData.tbsBytes.length.toString();
    const publicKeyAlgoOID = cert.subjectPublicKeyInfo.algorithm.algorithmId;
    const publicKeyAlgoFN = getFriendlyName(publicKeyAlgoOID);
    const signatureAlgoOID = cert.signatureAlgorithm.algorithmId;
    const signatureAlgoFN = getFriendlyName(signatureAlgoOID);
    certificateData.hashAlgorithm = getHashAlgorithm(signatureAlgoFN);
    certificateData.publicKeyAlgoOID = publicKeyAlgoOID;
    let params;
    if (publicKeyAlgoFN === "RSA" && signatureAlgoFN != "RSASSA_PSS") {
      certificateData.signatureAlgorithm = "rsa";
      params = getParamsRSA(cert);
    } else if (publicKeyAlgoFN === "ECC") {
      certificateData.signatureAlgorithm = "ecdsa";
      params = getParamsECDSA(cert);
    } else if (publicKeyAlgoFN === "RSASSA_PSS" || signatureAlgoFN === "RSASSA_PSS") {
      certificateData.signatureAlgorithm = "rsapss";
      params = getParamsRSAPSS(cert);
    } else {
      console.log(publicKeyAlgoFN);
    }
    certificateData.publicKeyDetails = params;
    certificateData.issuer = getIssuerCountryCode(cert);
    certificateData.validity = {
      notBefore: cert.notBefore.value.toString(),
      notAfter: cert.notAfter.value.toString()
    };
    const ski = getSubjectKeyIdentifier(cert);
    certificateData.id = ski.slice(0, 12);
    certificateData.subjectKeyIdentifier = ski;
    certificateData.rawPem = pem;
    const authorityKeyIdentifier = getAuthorityKeyIdentifier(cert);
    certificateData.authorityKeyIdentifier = authorityKeyIdentifier;
    if (certificateData.signatureAlgorithm === "rsapss" && (!certificateData.hashAlgorithm || certificateData.hashAlgorithm === "unknown")) {
      certificateData.hashAlgorithm = certificateData.publicKeyDetails.hashAlgorithm;
    }
    return certificateData;
  } catch (error) {
    console.error(`Error processing certificate`, error);
    throw error;
  }
}

// src/utils/types.ts
function isAadhaarDocument(passportData) {
  return passportData.documentCategory === "aadhaar";
}
function isKycDocument(passportData) {
  return passportData.documentCategory === "kyc";
}

// src/utils/circuits/circuitsName.ts
function getCircuitNameFromPassportData(passportData, circuitType) {
  if (circuitType === "register") {
    return getRegisterNameFromPassportData(passportData);
  } else {
    return getDSCircuitNameFromPassportData(passportData);
  }
}
function getDSCircuitNameFromPassportData(passportData) {
  console.log("Getting DSC circuit name from passport data...");
  if (isKycDocument(passportData)) {
    throw new Error("KYC documents do not have a DSC circuit");
  }
  if (passportData.documentCategory === "aadhaar") {
    throw new Error("Aadhaar does not have a DSC circuit");
  }
  if (!passportData.passportMetadata) {
    console.error("Passport metadata is missing");
    throw new Error("Passport data are not parsed");
  }
  const passportMetadata = passportData.passportMetadata;
  if (!passportMetadata.cscaFound) {
    console.error("CSCA not found in passport metadata");
    throw new Error("CSCA not found");
  }
  const signatureAlgorithm = passportMetadata.cscaSignatureAlgorithm;
  const hashFunction = passportMetadata.cscaHashFunction;
  console.log("CSCA Signature Algorithm:", signatureAlgorithm);
  console.log("CSCA Hash Function:", hashFunction);
  if (signatureAlgorithm === "ecdsa") {
    console.log("Processing ECDSA signature...");
    const curve = passportMetadata.cscaCurveOrExponent;
    console.log("ECDSA curve:", curve);
    const circuitName = `dsc_${hashFunction}_${signatureAlgorithm}_${curve}`;
    console.log("Generated circuit name:", circuitName);
    return circuitName;
  } else if (signatureAlgorithm === "rsa") {
    console.log("Processing RSA signature...");
    const exponent = passportMetadata.cscaCurveOrExponent;
    const bits = passportMetadata.cscaSignatureAlgorithmBits;
    console.log("RSA exponent:", exponent);
    console.log("RSA bits:", bits);
    if (bits <= 4096) {
      const circuitName = `dsc_${hashFunction}_${signatureAlgorithm}_${exponent}_${4096}`;
      console.log("Generated circuit name:", circuitName);
      return circuitName;
    } else {
      console.error("RSA key length exceeds maximum supported length");
      throw new Error(`Unsupported key length: ${bits}`);
    }
  } else if (signatureAlgorithm === "rsapss") {
    console.log("Processing RSA-PSS signature...");
    const exponent = passportMetadata.cscaCurveOrExponent;
    const saltLength = passportMetadata.cscaSaltLength;
    const bits = passportMetadata.cscaSignatureAlgorithmBits;
    console.log("RSA-PSS exponent:", exponent);
    console.log("RSA-PSS salt length:", saltLength);
    console.log("RSA-PSS bits:", bits);
    if (bits <= 4096) {
      const circuitName = `dsc_${hashFunction}_${signatureAlgorithm}_${exponent}_${saltLength}_${bits}`;
      console.log("Generated circuit name:", circuitName);
      return circuitName;
    } else {
      console.error("RSA-PSS key length exceeds maximum supported length");
      throw new Error(`Unsupported key length: ${bits}`);
    }
  } else {
    console.error("Unsupported signature algorithm:", signatureAlgorithm);
    throw new Error("Unsupported signature algorithm");
  }
}
function getRegisterNameFromPassportData(passportData) {
  console.log("Getting register circuit name from passport data...");
  if (passportData.documentCategory === "aadhaar") {
    return "register_aadhaar";
  }
  if (isKycDocument(passportData)) {
    return "register_kyc";
  }
  if (!passportData.passportMetadata) {
    console.error("Passport metadata is missing");
    throw new Error("Passport data are not parsed");
  }
  const passportMetadata = passportData.passportMetadata;
  if (!passportMetadata.cscaFound) {
    console.error("CSCA not found in passport metadata");
    throw new Error("CSCA not found");
  }
  const dgHashAlgo = passportMetadata.dg1HashFunction;
  const eContentHashAlgo = passportMetadata.eContentHashFunction;
  const signedAttrHashAlgo = passportMetadata.signedAttrHashFunction;
  const sigAlg = passportMetadata.signatureAlgorithm;
  console.log("DG Hash Algorithm:", dgHashAlgo);
  console.log("eContent Hash Algorithm:", eContentHashAlgo);
  console.log("Signed Attributes Hash Algorithm:", signedAttrHashAlgo);
  console.log("Signature Algorithm:", sigAlg);
  const prefix = passportData.documentType === "id_card" || passportData.documentType === "mock_id_card" ? "register_id" : "register";
  if (sigAlg === "ecdsa") {
    console.log("Processing ECDSA signature...");
    const { curveOrExponent } = passportMetadata;
    console.log("ECDSA curve:", curveOrExponent);
    const circuitName = `${prefix}_${dgHashAlgo}_${eContentHashAlgo}_${signedAttrHashAlgo}_${sigAlg}_${curveOrExponent}`;
    console.log("Generated circuit name:", circuitName);
    return circuitName;
  } else if (sigAlg === "rsa") {
    console.log("Processing RSA signature...");
    const { curveOrExponent, signatureAlgorithmBits } = passportMetadata;
    console.log("RSA exponent:", curveOrExponent);
    console.log("RSA bits:", signatureAlgorithmBits);
    if (signatureAlgorithmBits <= 4096) {
      const circuitName = `${prefix}_${dgHashAlgo}_${eContentHashAlgo}_${signedAttrHashAlgo}_${sigAlg}_${curveOrExponent}_${4096}`;
      console.log("Generated circuit name:", circuitName);
      return circuitName;
    } else {
      console.error("RSA key length exceeds maximum supported length");
      throw new Error(`Unsupported key length: ${signatureAlgorithmBits}`);
    }
  } else if (sigAlg === "rsapss") {
    console.log("Processing RSA-PSS signature...");
    const { curveOrExponent, saltLength, signatureAlgorithmBits } = passportMetadata;
    console.log("RSA-PSS exponent:", curveOrExponent);
    console.log("RSA-PSS salt length:", saltLength);
    console.log("RSA-PSS bits:", signatureAlgorithmBits);
    if (signatureAlgorithmBits <= 4096) {
      const circuitName = `${prefix}_${dgHashAlgo}_${eContentHashAlgo}_${signedAttrHashAlgo}_${sigAlg}_${curveOrExponent}_${saltLength}_${signatureAlgorithmBits}`;
      console.log("Generated circuit name:", circuitName);
      return circuitName;
    } else {
      console.error("RSA-PSS key length exceeds maximum supported length");
      throw new Error(`Unsupported key length: ${signatureAlgorithmBits}`);
    }
  } else {
    console.error("Unsupported signature algorithm:", sigAlg);
    throw new Error("Unsupported signature algorithm");
  }
}

// src/utils/hash.ts
import { ethers } from "ethers";
import { sha1 } from "js-sha1";
import { sha224, sha256 as sha2562 } from "js-sha256";
import { sha384, sha512 } from "js-sha512";
import * as forge from "node-forge";
import {
  poseidon1,
  poseidon2,
  poseidon3,
  poseidon4,
  poseidon5,
  poseidon6,
  poseidon7,
  poseidon8,
  poseidon9,
  poseidon10,
  poseidon11,
  poseidon12,
  poseidon13,
  poseidon14,
  poseidon15,
  poseidon16
} from "poseidon-lite";

// src/utils/bytes.ts
function computeIntChunkLength(byteLength) {
  const packSize = MAX_BYTES_IN_FIELD;
  const remain = byteLength % packSize;
  let numChunks = (byteLength - remain) / packSize;
  if (remain > 0) {
    numChunks += 1;
  }
  return numChunks;
}
function hexToSignedBytes(hexString) {
  const bytes = [];
  for (let i = 0; i < hexString.length - 1; i += 2) {
    const byte = parseInt(hexString.substr(i, 2), 16);
    bytes.push(byte >= 128 ? byte - 256 : byte);
  }
  return bytes;
}
function packBytesArray(unpacked) {
  const packSize = MAX_BYTES_IN_FIELD;
  const maxBytes = unpacked.length;
  const maxInts = computeIntChunkLength(maxBytes);
  const out = new Array(maxInts).fill(0n);
  for (let i = 0; i < maxInts; i++) {
    let sum = 0n;
    for (let j = 0; j < packSize; j++) {
      const idx = packSize * i + j;
      if (idx >= maxBytes) {
        continue;
      } else if (j === 0) {
        sum = BigInt(unpacked[idx]);
      } else {
        sum += (1n << BigInt(8 * j)) * BigInt(unpacked[idx]);
      }
    }
    out[i] = sum;
  }
  return out;
}

// src/utils/hash.ts
function customHasher(pubKeyFormatted) {
  if (pubKeyFormatted.length < 16) {
    return flexiblePoseidon(pubKeyFormatted.map(BigInt)).toString();
  } else {
    const rounds = Math.ceil(pubKeyFormatted.length / 16);
    if (rounds > 16) {
      throw new Error("Number of rounds is greater than 16");
    }
    const hash3 = new Array(rounds);
    for (let i = 0; i < rounds; i++) {
      hash3[i] = { inputs: new Array(16).fill(BigInt(0)) };
    }
    for (let i = 0; i < rounds; i++) {
      for (let j = 0; j < 16; j++) {
        if (i * 16 + j < pubKeyFormatted.length) {
          hash3[i].inputs[j] = BigInt(pubKeyFormatted[i * 16 + j]);
        }
      }
    }
    const finalHash = flexiblePoseidon(hash3.map((h) => poseidon16(h.inputs)));
    return finalHash.toString();
  }
}
function flexiblePoseidon(inputs) {
  switch (inputs.length) {
    case 1:
      return poseidon1(inputs);
    case 2:
      return poseidon2(inputs);
    case 3:
      return poseidon3(inputs);
    case 4:
      return poseidon4(inputs);
    case 5:
      return poseidon5(inputs);
    case 6:
      return poseidon6(inputs);
    case 7:
      return poseidon7(inputs);
    case 8:
      return poseidon8(inputs);
    case 9:
      return poseidon9(inputs);
    case 10:
      return poseidon10(inputs);
    case 11:
      return poseidon11(inputs);
    case 12:
      return poseidon12(inputs);
    case 13:
      return poseidon13(inputs);
    case 14:
      return poseidon14(inputs);
    case 15:
      return poseidon15(inputs);
    case 16:
      return poseidon16(inputs);
    default:
      throw new Error(`Unsupported number of inputs: ${inputs.length}`);
  }
}
function hash2(hashFunction, bytesArray, format = "bytes") {
  const unsignedBytesArray = bytesArray.map((byte) => byte & 255);
  let hashResult;
  switch (hashFunction) {
    case "sha1":
      hashResult = sha1(unsignedBytesArray);
      break;
    case "sha224":
      hashResult = sha224(unsignedBytesArray);
      break;
    case "sha256":
      hashResult = sha2562(unsignedBytesArray);
      break;
    case "sha384":
      hashResult = sha384(unsignedBytesArray);
      break;
    case "sha512":
      hashResult = sha512(unsignedBytesArray);
      break;
    default:
      console.log("\x1B[31m%s\x1B[0m", `${hashFunction} not found in hash`);
      hashResult = sha2562(unsignedBytesArray);
  }
  if (format === "hex") {
    return hashResult;
  }
  if (format === "bytes") {
    return hexToSignedBytes(hashResult);
  }
  const actualForgeUtil = forge.util ? forge.util : forge.default.util;
  if (format === "binary") {
    return actualForgeUtil.binary.raw.encode(new Uint8Array(hexToSignedBytes(hashResult)));
  }
  throw new Error(`Invalid format: ${format}`);
}
function packBytesAndPoseidon(unpacked) {
  const packed = packBytesArray(unpacked);
  return customHasher(packed.map(String)).toString();
}

// src/utils/passports/format.ts
function formatMrz(mrz) {
  const mrzCharcodes = [...mrz].map((char) => char.charCodeAt(0));
  if (mrz.length === 88) {
    mrzCharcodes.unshift(88);
    mrzCharcodes.unshift(95, 31);
    mrzCharcodes.unshift(91);
    mrzCharcodes.unshift(97);
  } else if (mrz.length === 90) {
    mrzCharcodes.unshift(90);
    mrzCharcodes.unshift(95, 31);
    mrzCharcodes.unshift(93);
    mrzCharcodes.unshift(97);
  } else {
    throw new Error(`Unsupported MRZ length: ${mrz.length}. Expected 88 or 90 characters.`);
  }
  return mrzCharcodes;
}

// src/utils/trees.ts
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import {
  poseidon2 as poseidon22,
  poseidon3 as poseidon32,
  poseidon4 as poseidon42,
  poseidon5 as poseidon54,
  poseidon6 as poseidon62,
  poseidon8 as poseidon82,
  poseidon10 as poseidon102,
  poseidon12 as poseidon122,
  poseidon13 as poseidon132
} from "poseidon-lite";

// src/utils/passports/passport.ts
import { sha256 as sha2563 } from "js-sha256";
import forge4 from "node-forge";
import { poseidon5 as poseidon53 } from "poseidon-lite";

// src/utils/aadhaar/mockData.ts
import forge3 from "node-forge";
import { poseidon5 as poseidon52 } from "poseidon-lite";

// src/utils/shaPad.ts
function assert(cond, errorMessage) {
  if (!cond) {
    throw new Error(errorMessage);
  }
}
function int64toBytes(num) {
  const arr = new ArrayBuffer(8);
  const view = new DataView(arr);
  view.setInt32(4, num, false);
  return new Uint8Array(arr);
}
function int128toBytes(x) {
  const buffer = new ArrayBuffer(16);
  const view = new DataView(buffer);
  view.setBigUint64(0, BigInt(0), false);
  view.setBigUint64(8, BigInt(x), false);
  return new Uint8Array(buffer);
}
function int8toBytes(num) {
  const arr = new ArrayBuffer(1);
  const view = new DataView(arr);
  view.setUint8(0, num);
  return new Uint8Array(arr);
}
function mergeUInt8Arrays(a1, a2) {
  const mergedArray = new Uint8Array(a1.length + a2.length);
  mergedArray.set(a1);
  mergedArray.set(a2, a1.length);
  return new Uint8Array(mergedArray.buffer);
}
function sha384_512Pad(prehash_prepad_m_array, maxShaBytes) {
  let prehash_prepad_m = new Uint8Array(prehash_prepad_m_array);
  const length_bits = prehash_prepad_m.length * 8;
  const length_in_bytes = int128toBytes(length_bits);
  prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, int8toBytes(2 ** 7));
  while ((prehash_prepad_m.length * 8 + length_in_bytes.length * 8) % 1024 !== 0) {
    prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, int8toBytes(0));
  }
  prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, length_in_bytes);
  assert(prehash_prepad_m.length * 8 % 1024 === 0, "Padding did not complete properly!");
  const messageLen = prehash_prepad_m.length;
  while (prehash_prepad_m.length < maxShaBytes) {
    prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, int128toBytes(0));
  }
  assert(
    prehash_prepad_m.length === maxShaBytes,
    `Padding to max length did not complete properly! Your padded message is ${prehash_prepad_m.length} long but max is ${maxShaBytes}!`
  );
  return [Array.from(prehash_prepad_m), messageLen];
}
function shaPad(prehash_prepad_m_array, maxShaBytes) {
  let prehash_prepad_m = new Uint8Array(prehash_prepad_m_array);
  const length_bits = prehash_prepad_m.length * 8;
  const length_in_bytes = int64toBytes(length_bits);
  prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, int8toBytes(2 ** 7));
  while ((prehash_prepad_m.length * 8 + length_in_bytes.length * 8) % 512 !== 0) {
    prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, int8toBytes(0));
  }
  prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, length_in_bytes);
  assert(prehash_prepad_m.length * 8 % 512 === 0, "Padding did not complete properly!");
  const messageLen = prehash_prepad_m.length;
  while (prehash_prepad_m.length < maxShaBytes) {
    prehash_prepad_m = mergeUInt8Arrays(prehash_prepad_m, int64toBytes(0));
  }
  assert(
    prehash_prepad_m.length === maxShaBytes,
    `Padding to max length did not complete properly! Your padded message is ${prehash_prepad_m.length} long but max is ${maxShaBytes}!`
  );
  return [Array.from(prehash_prepad_m), messageLen];
}

// src/utils/aadhaar/utils.ts
import { ethers as ethers2 } from "ethers";
import forge2 from "node-forge";
import {
  convertBigIntToByteArray,
  decompressByteArray,
  extractPhoto,
  getEndIndex,
  getRandomBytes,
  IdFields,
  rawDataToCompressedQR,
  replaceBytesBetween,
  returnFullId
} from "@anon-aadhaar/core";
var FIELD_POSITIONS = {
  REFERENCE_ID: 2,
  NAME: 3,
  DOB: 4,
  GENDER: 5,
  PINCODE: 11,
  STATE: 13,
  PHONE_NO: 17,
  PHOTO: 18
};
function extractQRDataFields(qrData) {
  let qrDataBytes;
  if (typeof qrData === "string") {
    qrDataBytes = convertBigIntToByteArray(BigInt(qrData));
  } else {
    qrDataBytes = qrData;
  }
  const decodedData = decompressByteArray(qrDataBytes);
  const signedData = decodedData.slice(0, decodedData.length - 256);
  const delimiterIndices = [];
  for (let i = 0; i < signedData.length; i++) {
    if (signedData[i] === 255) {
      delimiterIndices.push(i);
      if (delimiterIndices.length === 18) {
        break;
      }
    }
  }
  if (delimiterIndices.length < 18) {
    throw new Error(`Insufficient delimiters found: ${delimiterIndices.length}/18`);
  }
  const aadhaarLast4Digits = asciiArrayToString([
    signedData[5],
    signedData[6],
    signedData[7],
    signedData[8]
  ]);
  const nameData = extractFieldData(signedData, delimiterIndices, FIELD_POSITIONS.NAME);
  const name = asciiArrayToString(nameData).trim();
  const dobData = extractFieldData(signedData, delimiterIndices, FIELD_POSITIONS.DOB);
  const dob = asciiArrayToString([dobData[0], dobData[1]]);
  const mob = asciiArrayToString([dobData[3], dobData[4]]);
  const yob = asciiArrayToString([dobData[6], dobData[7], dobData[8], dobData[9]]);
  const genderData = extractFieldData(signedData, delimiterIndices, FIELD_POSITIONS.GENDER);
  const gender = asciiArrayToString(genderData);
  const pincodeData = extractFieldData(signedData, delimiterIndices, FIELD_POSITIONS.PINCODE);
  const pincode = asciiArrayToString(pincodeData);
  const stateData = extractFieldData(signedData, delimiterIndices, FIELD_POSITIONS.STATE);
  const state = asciiArrayToString(stateData).trim();
  const phoneData = extractFieldData(signedData, delimiterIndices, FIELD_POSITIONS.PHONE_NO);
  const phoneNoLast4Digits = asciiArrayToString(phoneData.slice(phoneData.length - 4));
  const timestampYear = asciiArrayToString([
    signedData[9],
    signedData[10],
    signedData[11],
    signedData[12]
  ]);
  const timestampMonth = asciiArrayToString([signedData[13], signedData[14]]);
  const timestampDay = asciiArrayToString([signedData[15], signedData[16]]);
  const timestampHour = asciiArrayToString([signedData[17], signedData[18]]);
  const timestampMinute = asciiArrayToString([signedData[19], signedData[20]]);
  const timestamp = `${timestampYear}-${timestampMonth}-${timestampDay} ${timestampHour}:${timestampMinute}`;
  return {
    name,
    yob,
    mob,
    dob,
    gender,
    pincode,
    state,
    aadhaarLast4Digits,
    phoneNoLast4Digits,
    timestamp
  };
}
function asciiArrayToString(asciiArray) {
  return asciiArray.filter((byte) => byte !== 0).map((byte) => String.fromCharCode(byte)).join("");
}
function extractFieldData(data, delimiterIndices, position) {
  const startIndex = delimiterIndices[position - 1] + 1;
  const endIndex = delimiterIndices[position];
  const fieldData = [];
  for (let i = startIndex; i < endIndex; i++) {
    fieldData.push(data[i]);
  }
  return fieldData;
}
function stringToAsciiArray(str) {
  return str.split("").map((char) => char.charCodeAt(0));
}

// src/utils/aadhaar/mockData.ts
import {
  convertBigIntToByteArray as convertBigIntToByteArray2,
  decompressByteArray as decompressByteArray2,
  extractPhoto as extractPhoto2,
  splitToWords
} from "@anon-aadhaar/core";
import { bufferToHex, Uint8ArrayToCharArray } from "@zk-email/helpers/dist/binary-format.js";
function computeCommitment(secret, qrHash, nullifier, packedCommitment, photoHash) {
  return poseidon52([secret, qrHash, nullifier, packedCommitment, photoHash]);
}
function computePackedCommitment(extractedFields) {
  const packedCommitmentArgs = [
    3,
    ...stringToAsciiArray(extractedFields.pincode),
    ...stringToAsciiArray(extractedFields.state.padEnd(31, "\0")),
    ...stringToAsciiArray(extractedFields.phoneNoLast4Digits),
    ...stringToAsciiArray(extractedFields.name.padEnd(62, "\0"))
  ];
  return BigInt(packBytesAndPoseidon(packedCommitmentArgs));
}
function nullifierHash(extractedFields) {
  const genderAscii = stringToAsciiArray(extractedFields.gender)[0];
  const personalInfoHashArgs = [
    genderAscii,
    ...stringToAsciiArray(extractedFields.yob),
    ...stringToAsciiArray(extractedFields.mob),
    ...stringToAsciiArray(extractedFields.dob),
    ...stringToAsciiArray(extractedFields.name.toUpperCase().padEnd(62, "\0")),
    ...stringToAsciiArray(extractedFields.aadhaarLast4Digits)
  ];
  return BigInt(packBytesAndPoseidon(personalInfoHashArgs));
}
function processQRDataSimple(qrData) {
  const qrDataBytes = convertBigIntToByteArray2(BigInt(qrData));
  const decodedData = decompressByteArray2(qrDataBytes);
  const signedData = decodedData.slice(0, decodedData.length - 256);
  const [qrDataPaddedNumber, qrDataPaddedLen] = shaPad(signedData, 512 * 3);
  const qrDataPadded = new Uint8Array(qrDataPaddedNumber);
  let photoEOI = 0;
  for (let i = 0; i < qrDataPadded.length - 1; i++) {
    if (qrDataPadded[i + 1] === 217 && qrDataPadded[i] === 255) {
      photoEOI = i + 1;
    }
  }
  if (photoEOI === 0) {
    throw new Error("Photo EOI not found");
  }
  const extractedFields = extractQRDataFields(qrDataBytes);
  const qrDataWithoutTimestamp = [
    ...Array.from(qrDataPadded.slice(0, 9)),
    ...Array.from(qrDataPadded.slice(9, 26)).map((x) => 0),
    ...Array.from(qrDataPadded.slice(26))
  ];
  const qrHash = packBytesAndPoseidon(qrDataWithoutTimestamp);
  const photo = extractPhoto2(Array.from(qrDataPadded), photoEOI + 1);
  const photoHash = packBytesAndPoseidon(photo.bytes.map(Number));
  return {
    qrDataBytes,
    decodedData,
    signedData,
    qrDataPadded,
    qrDataPaddedLen,
    extractedFields,
    qrHash: BigInt(qrHash),
    photo,
    photoHash: BigInt(photoHash)
  };
}

// src/utils/kyc/constants.ts
var KYC_COUNTRY_INDEX = 0;
var KYC_COUNTRY_LENGTH = 3;
var KYC_ID_TYPE_INDEX = KYC_COUNTRY_INDEX + KYC_COUNTRY_LENGTH;
var KYC_ID_TYPE_LENGTH = 27;
var KYC_ID_NUMBER_INDEX = KYC_ID_TYPE_INDEX + KYC_ID_TYPE_LENGTH;
var KYC_ID_NUMBER_LENGTH = 32;
var KYC_ISSUANCE_DATE_INDEX = KYC_ID_NUMBER_INDEX + KYC_ID_NUMBER_LENGTH;
var KYC_ISSUANCE_DATE_LENGTH = 8;
var KYC_EXPIRY_DATE_INDEX = KYC_ISSUANCE_DATE_INDEX + KYC_ISSUANCE_DATE_LENGTH;
var KYC_EXPIRY_DATE_LENGTH = 8;
var KYC_FULL_NAME_INDEX = KYC_EXPIRY_DATE_INDEX + KYC_EXPIRY_DATE_LENGTH;
var KYC_FULL_NAME_LENGTH = 64;
var KYC_DOB_INDEX = KYC_FULL_NAME_INDEX + KYC_FULL_NAME_LENGTH;
var KYC_DOB_LENGTH = 8;
var KYC_PHOTO_HASH_INDEX = KYC_DOB_INDEX + KYC_DOB_LENGTH;
var KYC_PHOTO_HASH_LENGTH = 32;
var KYC_PHONE_NUMBER_INDEX = KYC_PHOTO_HASH_INDEX + KYC_PHOTO_HASH_LENGTH;
var KYC_PHONE_NUMBER_LENGTH = 12;
var KYC_GENDER_INDEX = KYC_PHONE_NUMBER_INDEX + KYC_PHONE_NUMBER_LENGTH;
var KYC_GENDER_LENGTH = 1;
var KYC_ADDRESS_INDEX = KYC_GENDER_INDEX + KYC_GENDER_LENGTH;
var KYC_ADDRESS_LENGTH = 100;
var KYC_MAX_LENGTH = KYC_ADDRESS_INDEX + KYC_ADDRESS_LENGTH;
var KYC_REVEAL_DATA_INDICES = {
  COUNTRY: 0,
  ID_TYPE: KYC_COUNTRY_LENGTH,
  ID_NUMBER: KYC_ID_TYPE_INDEX + KYC_ID_TYPE_LENGTH,
  ISSUANCE_DATE: KYC_ID_NUMBER_INDEX + KYC_ID_NUMBER_LENGTH,
  EXPIRY_DATE: KYC_ISSUANCE_DATE_INDEX + KYC_ISSUANCE_DATE_LENGTH,
  FULL_NAME: KYC_EXPIRY_DATE_INDEX + KYC_EXPIRY_DATE_LENGTH,
  DOB: KYC_FULL_NAME_INDEX + KYC_FULL_NAME_LENGTH,
  PHOTO_HASH: KYC_DOB_INDEX + KYC_DOB_LENGTH,
  PHONE_NUMBER: KYC_PHOTO_HASH_INDEX + KYC_PHOTO_HASH_LENGTH,
  GENDER: KYC_PHONE_NUMBER_INDEX + KYC_PHONE_NUMBER_LENGTH,
  ADDRESS: KYC_GENDER_INDEX + KYC_GENDER_LENGTH
};
var KYC_SELECTOR_BITS = {
  COUNTRY: Array.from({ length: KYC_COUNTRY_LENGTH }, (_, i) => i),
  ID_TYPE: Array.from({ length: KYC_ID_TYPE_LENGTH }, (_, i) => i + KYC_COUNTRY_LENGTH),
  ID_NUMBER: Array.from(
    { length: KYC_ID_NUMBER_LENGTH },
    (_, i) => i + KYC_ID_TYPE_INDEX + KYC_ID_TYPE_LENGTH
  ),
  ISSUANCE_DATE: Array.from(
    { length: KYC_ISSUANCE_DATE_LENGTH },
    (_, i) => i + KYC_ID_NUMBER_INDEX + KYC_ID_NUMBER_LENGTH
  ),
  EXPIRY_DATE: Array.from(
    { length: KYC_EXPIRY_DATE_LENGTH },
    (_, i) => i + KYC_ISSUANCE_DATE_INDEX + KYC_ISSUANCE_DATE_LENGTH
  ),
  FULL_NAME: Array.from(
    { length: KYC_FULL_NAME_LENGTH },
    (_, i) => i + KYC_EXPIRY_DATE_INDEX + KYC_EXPIRY_DATE_LENGTH
  ),
  DOB: Array.from(
    { length: KYC_DOB_LENGTH },
    (_, i) => i + KYC_FULL_NAME_INDEX + KYC_FULL_NAME_LENGTH
  ),
  PHOTO_HASH: Array.from(
    { length: KYC_PHOTO_HASH_LENGTH },
    (_, i) => i + KYC_DOB_INDEX + KYC_DOB_LENGTH
  ),
  PHONE_NUMBER: Array.from(
    { length: KYC_PHONE_NUMBER_LENGTH },
    (_, i) => i + KYC_PHOTO_HASH_INDEX + KYC_PHOTO_HASH_LENGTH
  ),
  GENDER: Array.from(
    { length: KYC_GENDER_LENGTH },
    (_, i) => i + KYC_PHONE_NUMBER_INDEX + KYC_PHONE_NUMBER_LENGTH
  ),
  ADDRESS: Array.from(
    { length: KYC_ADDRESS_LENGTH },
    (_, i) => i + KYC_GENDER_INDEX + KYC_GENDER_LENGTH
  )
};

// src/utils/passports/passport.ts
function generateCommitment(secret, attestation_id, passportData) {
  const passportMetadata = passportData.passportMetadata;
  const dg1_packed_hash = packBytesAndPoseidon(formatMrz(passportData.mrz));
  const eContent_shaBytes = hash2(
    passportMetadata.eContentHashFunction,
    Array.from(passportData.eContent),
    "bytes"
  );
  const eContent_packed_hash = packBytesAndPoseidon(
    eContent_shaBytes.map((byte) => byte & 255)
  );
  const dsc_hash = getLeafDscTree(passportData.dsc_parsed, passportData.csca_parsed);
  return poseidon53([
    secret,
    attestation_id,
    dg1_packed_hash,
    eContent_packed_hash,
    dsc_hash
  ]).toString();
}
function generateNullifier(passportData) {
  if (isAadhaarDocument(passportData)) {
    return nullifierHash(passportData.extractedFields);
  }
  if (isKycDocument(passportData)) {
    const raw = Buffer.from(passportData.serializedApplicantInfo, "base64");
    const dataPadded = Array.from(raw, (b) => Number(b));
    const idNumber = dataPadded.slice(
      KYC_ID_NUMBER_INDEX,
      KYC_ID_NUMBER_INDEX + KYC_ID_NUMBER_LENGTH
    );
    const nullifierInputs = [
      ...idNumber,
      ...dataPadded.slice(KYC_ID_TYPE_INDEX, KYC_ID_TYPE_INDEX + KYC_ID_TYPE_LENGTH)
    ];
    const nullifier = packBytesAndPoseidon(nullifierInputs);
    return nullifier;
  }
  const signedAttr_shaBytes = hash2(
    passportData.passportMetadata.signedAttrHashFunction,
    Array.from(passportData.signedAttr),
    "bytes"
  );
  const signedAttr_packed_hash = packBytesAndPoseidon(
    signedAttr_shaBytes.map((byte) => byte & 255)
  );
  return signedAttr_packed_hash;
}
function pad(hashFunction) {
  return hashFunction === "sha1" || hashFunction === "sha224" || hashFunction === "sha256" ? shaPad : sha384_512Pad;
}

// src/utils/trees.ts
import { IMT } from "@openpassport/zk-kit-imt";
import { LeanIMT } from "@openpassport/zk-kit-lean-imt";
import { SMT } from "@openpassport/zk-kit-smt";
countries.registerLocale(en);
function getLeaf(parsed, type) {
  if (type === "dsc") {
    const tbsArray = Object.keys(parsed.tbsBytes).map((key) => parsed.tbsBytes[key]);
    const [paddedTbsBytes, tbsBytesPaddedLength] = pad(parsed.hashAlgorithm)(
      tbsArray,
      max_dsc_bytes
    );
    const dsc_hash = packBytesAndPoseidon(Array.from(paddedTbsBytes));
    return poseidon22([dsc_hash, tbsArray.length]).toString();
  } else {
    const tbsBytesArray = Array.from(parsed.tbsBytes);
    const paddedTbsBytesArray = tbsBytesArray.concat(
      new Array(max_csca_bytes - tbsBytesArray.length).fill(0)
    );
    const csca_hash = packBytesAndPoseidon(paddedTbsBytesArray);
    return poseidon22([csca_hash, tbsBytesArray.length]).toString();
  }
}
function getLeafDscTree(dsc_parsed, csca_parsed) {
  const dscLeaf = getLeaf(dsc_parsed, "dsc");
  const cscaLeaf = getLeaf(csca_parsed, "csca");
  return poseidon22([dscLeaf, cscaLeaf]).toString();
}

// src/utils/kyc/utils.ts
import { poseidon2 as poseidon23 } from "poseidon-lite";
var decodeRawBytes = (base64) => {
  const raw = Buffer.from(base64, "base64");
  return Array.from(raw, (b) => Number(b));
};
var generateKycCommitment = (passportData, secret) => {
  if (isKycDocument(passportData)) {
    const dataPadded = decodeRawBytes(passportData.serializedApplicantInfo);
    const commitment = poseidon23([secret, packBytesAndPoseidon(dataPadded)]);
    return commitment.toString();
  }
};

// src/utils/passports/validate.ts
import { LeanIMT as LeanIMT2 } from "@openpassport/zk-kit-lean-imt";
function validateRegistrationCircuit(passportData, deployedCircuits) {
  let circuitNameRegister = getCircuitNameFromPassportData(
    passportData,
    "register"
  );
  const isValid = circuitNameRegister && (deployedCircuits.REGISTER.includes(circuitNameRegister) || deployedCircuits.REGISTER_ID.includes(circuitNameRegister) || deployedCircuits.REGISTER_AADHAAR.includes(circuitNameRegister) || deployedCircuits.REGISTER_KYC.includes(circuitNameRegister));
  return { isValid: !!isValid, circuitName: circuitNameRegister };
}
function validateDscCircuit(passportData, deployedCircuits) {
  const circuitNameDsc = getCircuitNameFromPassportData(passportData, "dsc");
  const isValid = circuitNameDsc && (deployedCircuits.DSC.includes(circuitNameDsc) || deployedCircuits.DSC_ID.includes(circuitNameDsc));
  return { isValid: !!isValid, circuitName: circuitNameDsc };
}
async function checkDocumentSupported(passportData, opts) {
  const deployedCircuits = opts.getDeployedCircuits(passportData.documentCategory);
  if (passportData.documentCategory === "aadhaar" || passportData.documentCategory === "kyc") {
    const { isValid, circuitName } = validateRegistrationCircuit(passportData, deployedCircuits);
    if (!isValid) {
      return {
        status: "registration_circuit_not_supported",
        details: circuitName
      };
    }
    return { status: "passport_supported", details: circuitName };
  }
  const passportMetadata = passportData.passportMetadata;
  if (!passportMetadata) {
    console.warn("Passport metadata is null");
    return { status: "passport_metadata_missing", details: passportData.dsc };
  }
  if (!passportMetadata.cscaFound) {
    console.warn("CSCA not found");
    return { status: "csca_not_found", details: passportData.dsc };
  }
  const { isValid: isRegisterValid, circuitName: registerCircuitName } = validateRegistrationCircuit(passportData, deployedCircuits);
  if (!isRegisterValid) {
    return {
      status: "registration_circuit_not_supported",
      details: registerCircuitName
    };
  }
  const { isValid: isDscValid, circuitName: dscCircuitName } = validateDscCircuit(
    passportData,
    deployedCircuits
  );
  if (!isDscValid) {
    console.warn("DSC circuit not supported:", dscCircuitName);
    return { status: "dsc_circuit_not_supported", details: dscCircuitName };
  }
  return { status: "passport_supported", details: dscCircuitName };
}
async function checkIfPassportDscIsInTree(passportData, dscTree) {
  const hashFunction = (a, b) => poseidon24([a, b]);
  const tree = LeanIMT2.import(hashFunction, dscTree);
  const leaf = getLeafDscTree(passportData.dsc_parsed, passportData.csca_parsed);
  const index = tree.indexOf(BigInt(leaf));
  if (index === -1) {
    console.warn("DSC not found in the tree");
    return false;
  }
  return true;
}
function generateCommitmentInApp(secret, attestation_id, passportData, alternativeCSCA) {
  const dg1_packed_hash = packBytesAndPoseidon(formatMrz(passportData.mrz));
  const eContent_packed_hash = packBytesAndPoseidon(
    hash2(
      passportData.passportMetadata.eContentHashFunction,
      Array.from(passportData.eContent),
      "bytes"
    ).map((byte) => byte & 255)
  );
  const csca_list = [];
  const commitment_list = [];
  for (const [cscaKey, cscaValue] of Object.entries(alternativeCSCA)) {
    try {
      const formattedCsca = formatCSCAPem(cscaValue);
      const cscaParsed = parseCertificateSimple(formattedCsca);
      const commitment = poseidon55([
        secret,
        attestation_id,
        dg1_packed_hash,
        eContent_packed_hash,
        getLeafDscTree(passportData.dsc_parsed, cscaParsed)
      ]).toString();
      csca_list.push(formatCSCAPem(cscaValue));
      commitment_list.push(commitment);
    } catch (error) {
      console.warn(`Failed to parse CSCA certificate for key ${cscaKey}:`, error);
    }
  }
  if (commitment_list.length === 0) {
    console.error("No valid CSCA certificates found in alternativeCSCA");
  }
  return { commitment_list, csca_list };
}
function generateCommitmentInAppAadhaar(secret, attestation_id, passportData, alternativePublicKeys) {
  const nullifier = nullifierHash(passportData.extractedFields);
  const packedCommitment = computePackedCommitment(passportData.extractedFields);
  const { qrHash, photoHash } = processQRDataSimple(passportData.qrData);
  const publicKey_list = [];
  const commitment_list = [];
  const allPublicKeys = {
    document_public_key: passportData.publicKey,
    ...alternativePublicKeys
  };
  for (const [keyName, publicKeyValue] of Object.entries(allPublicKeys)) {
    try {
      const commitment = computeCommitment(
        BigInt(secret),
        BigInt(qrHash),
        nullifier,
        packedCommitment,
        photoHash
      ).toString();
      publicKey_list.push(publicKeyValue);
      commitment_list.push(commitment);
    } catch (error) {
      console.warn(`Failed to process public key for ${keyName}:`, error);
    }
  }
  if (commitment_list.length === 0) {
    console.error("No valid public keys found for Aadhaar");
  }
  return { commitment_list, publicKey_list };
}
async function isDocumentNullified(passportData) {
  const nullifier = generateNullifier(passportData);
  const nullifierHex = `0x${BigInt(nullifier).toString(16)}`;
  const attestationId = passportData.documentCategory === "passport" ? "0x0000000000000000000000000000000000000000000000000000000000000001" /* passport */ : passportData.documentCategory === "aadhaar" ? "0x0000000000000000000000000000000000000000000000000000000000000003" /* aadhaar */ : passportData.documentCategory === "kyc" ? "0x0000000000000000000000000000000000000000000000000000000000000004" /* kyc */ : "0x0000000000000000000000000000000000000000000000000000000000000002" /* id_card */;
  console.log("checking for nullifier", nullifierHex, attestationId);
  const baseUrl = passportData.mock === false ? API_URL : API_URL_STAGING;
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), 3e4);
  try {
    const response = await fetch(`${baseUrl}/is-nullifier-onchain-with-attestation-id`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nullifier: nullifierHex, attestation_id: attestationId }),
      signal: controller.signal
    });
    clearTimeout(t);
    if (!response.ok) {
      throw new Error(`isDocumentNullified non-OK response: ${response.status}`);
    }
    const data = await response.json();
    return Boolean(data?.data);
  } catch (e) {
    const erorr = e instanceof Error ? e : new Error(String(e));
    clearTimeout(t);
    throw new Error(
      `isDocumentNullified request failed: ${erorr.name} ${erorr.message} 
 ${erorr.stack}`
    );
  }
}
async function isUserRegistered(documentData, secret, getCommitmentTree) {
  if (!documentData) {
    return false;
  }
  const document = documentData.documentCategory;
  let commitment;
  if (isKycDocument(documentData)) {
    commitment = generateKycCommitment(documentData, secret);
  } else if (document === "aadhaar") {
    const aadhaarData = documentData;
    const nullifier = nullifierHash(aadhaarData.extractedFields);
    const packedCommitment = computePackedCommitment(aadhaarData.extractedFields);
    const { qrHash, photoHash } = processQRDataSimple(aadhaarData.qrData);
    commitment = computeCommitment(
      BigInt(secret),
      BigInt(qrHash),
      nullifier,
      packedCommitment,
      photoHash
    ).toString();
    console.log("commitment", commitment);
  } else {
    const attestationId = document === "passport" ? PASSPORT_ATTESTATION_ID : ID_CARD_ATTESTATION_ID;
    commitment = generateCommitment(secret, attestationId, documentData);
  }
  const serializedTree = getCommitmentTree(document);
  const tree = LeanIMT2.import((a, b) => poseidon24([a, b]), serializedTree);
  const index = tree.indexOf(BigInt(commitment));
  return index !== -1;
}
async function isUserRegisteredWithAlternativeCSCA(passportData, secret, {
  getCommitmentTree,
  getAltCSCA
}) {
  if (!passportData) {
    console.error("Passport data is null");
    return { isRegistered: false, csca: null };
  }
  const document = passportData.documentCategory;
  let commitment_list;
  let csca_list;
  if (document === "kyc") {
    const isRegistered = await isUserRegistered(passportData, secret, getCommitmentTree);
    return { isRegistered, csca: null };
  }
  if (document === "aadhaar") {
    const publicKeys = getAltCSCA(document);
    if (!publicKeys || Object.keys(publicKeys).length === 0) {
      console.error("No public keys available for Aadhaar");
      return { isRegistered: false, csca: null };
    }
    const alternativePublicKeys = {};
    Object.entries(publicKeys).forEach(([key, value], index) => {
      alternativePublicKeys[`public_key_${index}`] = value;
    });
    const result = generateCommitmentInAppAadhaar(
      secret,
      "0x0000000000000000000000000000000000000000000000000000000000000003" /* aadhaar */,
      passportData,
      alternativePublicKeys
    );
    commitment_list = result.commitment_list;
    csca_list = result.publicKey_list;
  } else {
    const alternativeCSCA = getAltCSCA(document);
    const result = generateCommitmentInApp(
      secret,
      document === "passport" ? PASSPORT_ATTESTATION_ID : ID_CARD_ATTESTATION_ID,
      passportData,
      alternativeCSCA
    );
    commitment_list = result.commitment_list;
    csca_list = result.csca_list;
  }
  if (commitment_list.length === 0) {
    const errorMsg = document === "aadhaar" ? "No valid public keys could be processed for Aadhaar" : "No valid CSCA certificates could be parsed from alternativeCSCA";
    console.error(errorMsg);
    return { isRegistered: false, csca: null };
  }
  const serializedTree = getCommitmentTree(document);
  const tree = LeanIMT2.import((a, b) => poseidon24([a, b]), serializedTree);
  for (let i = 0; i < commitment_list.length; i++) {
    const commitment = commitment_list[i];
    const index = tree.indexOf(BigInt(commitment));
    if (index !== -1) {
      return { isRegistered: true, csca: csca_list[i] };
    }
  }
  const warnMsg = document === "aadhaar" ? `None of the following public keys correspond to the commitment for Aadhaar: ${csca_list}` : `None of the following CSCA correspond to the commitment: ${csca_list}`;
  console.warn(warnMsg);
  return { isRegistered: false, csca: null };
}
function formatCSCAPem(cscaPem) {
  let cleanedPem = cscaPem.trim();
  if (!cleanedPem.includes("-----BEGIN CERTIFICATE-----")) {
    cleanedPem = cleanedPem.replace(/[^A-Za-z0-9+/=]/g, "");
    try {
      Buffer.from(cleanedPem, "base64");
    } catch (error) {
      throw new Error(`Invalid base64 certificate data: ${error}`);
    }
    cleanedPem = `-----BEGIN CERTIFICATE-----
${cleanedPem}
-----END CERTIFICATE-----`;
  }
  return cleanedPem;
}
export {
  checkDocumentSupported,
  checkIfPassportDscIsInTree,
  generateCommitmentInApp,
  generateCommitmentInAppAadhaar,
  isDocumentNullified,
  isUserRegistered,
  isUserRegisteredWithAlternativeCSCA
};
//# sourceMappingURL=validate.js.map